import { Config } from 'config/envConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import axios from 'axios';
import { getFileIcon, getFileIconColor } from '@quyva/common';
import {
    faBoxOpen,
    faCopy,
    faEllipsisV,
    faFileExport,
    faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { ConfirmModal, Dropdown, LoadingView } from '@quyva/shared-ui';
import { getUrl } from '../pdfDisplay/utils';
import { useFeatureFlags } from '../../context';
import {
    Features,
    LayoutFeatures,
    getFileNameWithoutExt,
    isFeatureEnabled,
} from '@quyva/common';

const NoFileView = () => {
    return (
        <div className='w-full h-14 flex justify-center items-center'>
            <FontAwesomeIcon
                icon={faBoxOpen}
                className='mr-2 text-texts-secondary300'
            />
            <div className='text-texts-secondary300'>No layouts found</div>
        </div>
    );
};

const FilesListLoadingView = ({ loadingText }) => {
    return (
        <div className='w-full h-24 flex justify-center items-center'>
            <LoadingView loadingText={loadingText} inLine={false} />
        </div>
    );
};

const LayoutFilesListView = ({
    email,
    currentRepo,
    allTemplatesFilesData,
    selectedTemplate,
    setSelectedTemplate,
    selectedLayout,
    setSelectedLayout,
    getLayoutsList,
    closeSidebar,
    setTemplateWithAction,
    setAddNewTemplateMode,
    setRetrievedTemplateData,
    exportLayouts,
    closeDropdownSignal,
    setCloseDropdownSignal,
}) => {
    const { featureFlagsData } = useFeatureFlags();
    const user = JSON.parse(localStorage.getItem('current'));
    const [toDeleteLayout, setToDeleteLayout] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState(false);

    const deleteLayout = async () => {
        try {
            setShowDeleteModal(false);

            const layoutName = toDeleteLayout.name;
            const templateName = currentRepo.filename;

            setLoadingText(`Deleting ${layoutName}...`);
            setIsLoading(true);

            const encodedToken = encodeURIComponent(user.token);
            const dynamicUrl = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/deleteLayout?email=${email}&templateName=${templateName}&layoutName=${layoutName}`
            );

            const response = await axios.delete(dynamicUrl, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: encodedToken,
                },
            });

            if (response) {
                console.log(response.data.message);

                if (
                    selectedLayout &&
                    selectedLayout.name === toDeleteLayout.name
                ) {
                    setSelectedLayout(null);
                }

                setLoadingText('Loading Layout List...');
                await getLayoutsList(currentRepo.filename);
            }

            setIsLoading(false);
            setLoadingText(``);
            setTemplateWithAction(currentRepo.filename);
        } catch (error) {
            console.error('An error occurred while deleting layout:', error);
            setIsLoading(false);
            setLoadingText(``);
            setTemplateWithAction(currentRepo.filename);
        }
    };

    const copyLayout = async (layout) => {
        try {
            const layoutName = layout.name;
            const templateName = currentRepo.filename;

            setLoadingText(`Copying ${layoutName}...`);
            setIsLoading(true);

            const encodedToken = encodeURIComponent(user.token);
            const dynamicUrl = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/copyLayout?email=${email}&templateName=${templateName}&layoutName=${layoutName}`
            );

            const response = await axios.get(dynamicUrl, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: encodedToken,
                },
            });

            if (response) {
                console.log(response.data.message);

                setLoadingText('Loading Layout List...');
                await getLayoutsList(currentRepo.filename);
            }

            setIsLoading(false);
            setLoadingText(``);
            setTemplateWithAction(currentRepo.filename);
        } catch (error) {
            console.error('An error occurred while copying layout:', error);
            setIsLoading(false);
            setLoadingText(``);
            setTemplateWithAction(currentRepo.filename);
        }
    };

    const repoFileData =
        allTemplatesFilesData && allTemplatesFilesData.size
            ? allTemplatesFilesData.get(currentRepo.filename)
            : null;

    if (repoFileData) {
        const isExpanded = repoFileData.isExpanded;
        const filesData = repoFileData.filesData;

        // Layout action dropdown options
        const list = filesData.map((item, index) => {
            const LayoutActionList = [];
            if (
                isFeatureEnabled(
                    [Features.LAYOUT, LayoutFeatures.COPY_LAYOUT],
                    featureFlagsData
                )
            ) {
                LayoutActionList.push({
                    label: 'Copy Layout',
                    icon: faCopy,
                    iconColor: 'lightblue',
                    action: async () => {
                        copyLayout(item);
                    },
                    disabled: isLoading,
                    type: 'success',
                });
            }
            if (
                isFeatureEnabled(
                    [Features.LAYOUT, LayoutFeatures.EXPORT_LAYOUT],
                    featureFlagsData
                )
            ) {
                LayoutActionList.push({
                    label: 'Export Layout',
                    icon: faFileExport,
                    iconColor: 'lightblue',
                    action: async () => {
                        await exportLayouts(currentRepo.filename, [item.name]);
                    },
                    disabled: isLoading,
                    type: 'success',
                });
            }
            if (
                isFeatureEnabled(
                    [Features.LAYOUT, LayoutFeatures.DELETE_LAYOUT],
                    featureFlagsData
                )
            ) {
                LayoutActionList.push({
                    label: 'Delete Layout',
                    icon: faTrash,
                    iconColor: 'red',
                    action: async () => {
                        setShowDeleteModal(true);
                        setToDeleteLayout(item);
                    },
                    disabled: isLoading,
                    type: 'danger',
                });
            }

            return (
                <div
                    key={`${currentRepo.filename} ${item.name} ${index}`}
                    className={`flex justify-between items-end`}
                >
                    <div className='overflow-ellipsis overflow-hidden h-10 border-l-2 pr-2 flex justify-start items-center'>
                        <span
                            className='font-bold'
                            style={{ color: '#e6e6e8' }}
                        >
                            -
                        </span>

                        <div
                            className={`cursor-pointer border border-transparent hover:bg-backgrounds-primary100 hover:rounded-lg hover:border-backgrounds-primary200 px-2 truncate ${
                                selectedTemplate &&
                                currentRepo &&
                                selectedLayout &&
                                selectedTemplate.filename ===
                                    currentRepo.filename &&
                                selectedLayout.name === item.name
                                    ? 'bg-backgrounds-primary100 rounded-lg shadow-inner border'
                                    : 'bg-backgrounds-white'
                            }`}
                            onClick={(e) => {
                                setSelectedLayout(item);
                                setSelectedTemplate(currentRepo);
                                setAddNewTemplateMode(false);
                                setRetrievedTemplateData(null);
                                e.stopPropagation();
                                if (window.innerWidth < 1024) {
                                    closeSidebar(); // Call the closeSidebar function passed from navbar.jsx
                                }
                            }}
                        >
                            <FontAwesomeIcon
                                icon={getFileIcon(item.name)}
                                color={getFileIconColor(item.name)}
                                className='mr-2 drop-shadow-sm bg-backgrounds-white rounded-full'
                            />
                            <span
                                className='truncate text-base'
                                title={getFileNameWithoutExt(item.name)}
                            >
                                {getFileNameWithoutExt(item.name)}
                            </span>
                        </div>
                    </div>

                    <div className='h-10 flex justify-end items-center'>
                        <Dropdown
                            dropDownList={LayoutActionList}
                            icon={<FontAwesomeIcon icon={faEllipsisV} />}
                            showButtonText={false}
                            buttonText={null}
                            buttonHoverText={'More Template Actions'}
                            additionalButtonAction={() => {
                                setCloseDropdownSignal(false);
                            }}
                            titleText={'Template Actions'}
                            buttonClassName={`px-3.5 py-2 ml-2`}
                            backgroundColor={'white'}
                            textColor={'green'}
                            placement={'right'}
                            closeSignal={closeDropdownSignal}
                            listTitleText={'Layout Actions'}
                        />
                    </div>
                </div>
            );
        });

        if (isExpanded) {
            return (
                <div>
                    <div
                        className={`bg-backgrounds-white rounded-b-xl pl-4 pr-2 pb-3 truncate shadow-sm border`}
                    >
                        {isLoading ? (
                            <FilesListLoadingView loadingText={loadingText} />
                        ) : (
                            <>
                                {repoFileData &&
                                repoFileData.filesData &&
                                repoFileData.filesData.length ? (
                                    list
                                ) : (
                                    <NoFileView />
                                )}
                            </>
                        )}
                    </div>
                    {showDeleteModal && (
                        <ConfirmModal
                            modalText={`Delete '${getFileNameWithoutExt(
                                toDeleteLayout.name
                            )}' layout file?`}
                            onConfirm={async () => await deleteLayout()}
                            onCancel={() => setShowDeleteModal(false)}
                        />
                    )}
                </div>
            );
        }
    }
    return <></>;
};

export default LayoutFilesListView;
